import { useState, useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  MessageCircle, X, Send, Bot, User, Loader2, 
  Trash2, ChevronDown, Sparkles, History
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Export ChatToggleButton for use in Dashboard header
export const ChatToggleButton = () => {
  const handleToggle = () => {
    window.dispatchEvent(new CustomEvent('flowguard-chat-toggle'));
  };

  return (
    <Button
      variant="ghost"
      onClick={handleToggle}
      className="flex items-center gap-2 text-slate-300 hover:text-white"
      data-testid="chat-toggle-button"
      aria-label="Open AI Chat"
    >
      <MessageCircle className="h-4 w-4" />
      <span className="hidden sm:inline">AI Chat</span>
    </Button>
  );
};

const AIChatWidget = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(380);
  const minWidth = 320;
  const maxWidth = 720;
  const resizingRef = useRef(false);
  const hiddenOnPaths = ["/login", "/register", "/register/complete", "/checkout", "/auth/callback"];
  const appRoutePatterns = [
    "/",
    "/pricing",
    "/calculator",
    "/blog",
    "/blog/:slug",
    "/rental-calculator",
    "/brrrr",
    "/flip",
    "/airbnb",
    "/stress-testing",
    "/login",
    "/register",
    "/register/complete",
    "/checkout",
    "/auth/callback",
    "/onboarding",
    "/dashboard",
    "/quick-screen",
    "/admin",
    "/upgrade",
    "/upgrade/success",
    "/deals",
    "/deals/:dealId",
    "/reports",
    "/property-research",
    "/deals/:dealId/performance",
    "/partnerships",
    "/tax-strategy",
    "/renovation-estimator",
    "/rental-analysis",
    "/rental-analysis/:id",
    "/flip-calculator",
    "/airbnb-calculator",
    "/mortgage-calculator",
    "/brrrr-calculator",
    "/brrrr-calculator/:id",
    "/expense-templates",
    "/rent-estimator",
    "/portfolio",
    "/compare",
    "/1031-exchange",
    "/seller-financing",
    "/debt-paydown",
    "/settings",
    "/branding",
    "/report/:type/:id"
  ];

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setIsOpen(false);
    setShowHistory(false);
  }, [location.pathname]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle resizing via mouse drag
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizingRef.current) return;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, window.innerWidth - e.clientX));
      setSidebarWidth(newWidth);
      document.documentElement.style.setProperty('--chat-sidebar-width', `${newWidth}px`);
    };

    const onMouseUp = () => {
      if (resizingRef.current) resizingRef.current = false;
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isOpen]);

  const startResizing = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.userSelect = 'none';
  };

  // Shift page content when sidebar opens
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('--chat-sidebar-width', `${sidebarWidth}px`);
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }
    return () => {
      document.body.classList.remove('chat-open');
      document.documentElement.style.setProperty('--chat-sidebar-width', `0px`);
    };
  }, [isOpen, sidebarWidth]);

  // Listen for chat toggle event from ChatToggleButton
  useEffect(() => {
    const handleToggle = () => {
      setIsOpen(prev => {
        const willOpen = !prev;
        if (willOpen && conversations.length === 0) {
          loadConversations();
        }
        return willOpen;
      });
    };

    window.addEventListener('flowguard-chat-toggle', handleToggle);
    return () => {
      window.removeEventListener('flowguard-chat-toggle', handleToggle);
    };
  }, [conversations.length]);

  // Load conversations when opening history
  const loadConversations = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    }
  };

  // Load a specific conversation
  const loadConversation = async (convId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/chat/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setConversationId(convId);
      setShowHistory(false);
    } catch (error) {
      console.error("Failed to load conversation:", error);
      toast.error("Failed to load conversation");
    }
  };

  // Delete a conversation
  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!token) return;
    try {
      await axios.delete(`${API}/chat/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        setMessages([]);
        setConversationId(null);
      }
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  // Start a new chat
  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  // Send a message
  const sendMessage = async () => {
    if (!input.trim() || isLoading || !token) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/message`,
        {
          message: userMessage.content,
          conversation_id: conversationId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const assistantMessage = {
        id: response.data.message_id,
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(response.data.conversation_id);

    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
      // Remove the user message if failed
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't render if not logged in
  if (!user || !token) return null;
  if (hiddenOnPaths.includes(location.pathname)) return null;
  if (!appRoutePatterns.some((path) => matchPath({ path, end: true }, location.pathname))) return null;

  return (
    <>
      {/* Chat Sidebar - rendered on all valid app routes */}
      {isOpen && (
        <div
          className="fixed top-16 right-0 z-50 h-[calc(100vh-4rem)] bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col overflow-hidden"
          style={{ width: sidebarWidth + 'px', minWidth: minWidth }}
          data-testid="chat-sidebar"
        >
          {/* top compact header removed to avoid duplication - keep main gradient header below */}
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-slate-700 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">FlowGuard AI</h3>
                <p className="text-xs text-slate-400">Real Estate Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowHistory(!showHistory);
                  if (!showHistory) loadConversations();
                }}
                className="text-slate-400 hover:text-white h-8 w-8 p-0"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={startNewChat}
                className="text-slate-400 hover:text-white h-8 w-8 p-0"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-white h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300">Chat History</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startNewChat}
                  className="text-amber-500 hover:text-amber-400 text-xs"
                >
                  + New Chat
                </Button>
              </div>
              {conversations.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No conversations yet</p>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center justify-between group ${
                      conversationId === conv.id 
                        ? 'bg-amber-500/20 border border-amber-500/30' 
                        : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{conv.title}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => deleteConversation(conv.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-amber-500" />
                    </div>
                    <h4 className="text-white font-medium mb-2">Hi, I'm FlowGuard AI!</h4>
                    <p className="text-sm text-slate-400 mb-4">
                      I can help you with real estate investing, tax strategies, and navigating the platform.
                    </p>
                    <div className="space-y-2">
                      {[
                        "What is a good cap rate?",
                        "Explain the BRRRR strategy",
                        "How does a 1031 exchange work?",
                        "Show me available tools"
                      ].map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => setInput(suggestion)}
                          className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <div key={msg.id || idx} className="w-full">
                    {msg.role === 'assistant' ? (
                      <div className="w-full bg-slate-800 text-slate-200 rounded-xl p-4 mb-2 border border-slate-700">
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <div className="max-w-[75%] bg-amber-500 text-white rounded-2xl px-4 py-2.5 rounded-br-md">
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-800 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                        <span className="text-sm text-slate-400">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-slate-700">
                <div className="flex items-center gap-2 bg-slate-800 rounded-xl px-3 py-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about real estate investing..."
                    className="flex-1 bg-transparent text-white text-sm placeholder-slate-500 outline-none"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!input.trim() || isLoading}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-white h-8 w-8 p-0 rounded-lg disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Educational information only, not financial, investment, tax, or
                  legal advice. AI can make mistakes; verify important information
                  with qualified professionals.
                </p>
              </div>
            </>
          )}
          {/* Resize handle */}
          <div
            onMouseDown={(e) => startResizing(e)}
            className="absolute left-[-6px] top-0 h-full w-3 cursor-ew-resize z-50"
          />
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
